import { CalendarDate } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { CircleInfoIcon } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import Divider from 'dashboard/components/Divider';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { ExporterFileType } from 'dashboard/models/exporter';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { MetricsGroupBy } from 'dashboard/models/metrics';
import { Tag } from 'dashboard/models/tag';
import { Template } from 'dashboard/models/template';
import { MetricsFilterParams } from 'dashboard/redux/actions/metrics-inquiry';
import { DynamicFilterSet } from 'dashboard/redux/reducers/metrics-inquiry';
import { findSelectedTags } from 'dashboard/routes/DashboardInquiryAnalyticsVolumeRoute/DashboardInquiryAnalyticsVolumeRoute';
import { FilterDimensions } from 'dashboard/views/DashboardInquiryAnalyticsVolumeV2View/InquiryMetrics';
import Banner from 'design-system/components/Banner';
import Button, { ButtonProps } from 'design-system/components/Button';
import DateField from 'design-system/components/DatePicker/DateField';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useToggle from 'lib/hooks/useToggle';
import { nameDisplay } from 'lib/utils/template';

const HC_ARTICLE =
  'https://help.withpersona.com/articles/3UMDh6jIJV8OaqMHKfFpZQ/inquiry-overview-export/';

const WarningIcon = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${Spacing[3]};
`;

const DateRangeInputs = styled.div`
  display: flex;
`;

const StyledDivider = styled(Divider)`
  margin: 0;
`;

const ExportButtonGroup = styled(HStack)`
  margin-left: auto;
`;

interface InquiryAnalyticsExportModalProps {
  lastQueryFilters: MetricsFilterParams;
  templates: (Template | InquiryTemplate)[];
  dynamicFilters: DynamicFilterSet;
  warningModal: ReactNode | null;
  inquiryTags: Tag[];
  confirmButtonType?: ButtonProps['variant'];
  cancelAction: () => void;
  confirmAction: (exporterFileType: ExporterFileType) => void;
}

const InquiryAnalyticsExportModal: FC<InquiryAnalyticsExportModalProps> = ({
  lastQueryFilters,
  dynamicFilters,
  warningModal,
  templates,
  inquiryTags,
  confirmButtonType = 'danger',
  cancelAction,
  confirmAction,
}) => {
  const [isPending, { enable, disable }] = useToggle(false);

  const [exporterFileType, setExporterFileType] = useState(ExporterFileType.TarGz);

  const handleConfirm = useCallback(async () => {
    enable();
    confirmAction(exporterFileType);
    disable();
    // close modal on complete
    cancelAction();
  }, [cancelAction, confirmAction, disable, enable, exporterFileType]);

  const createdAtStart = lastQueryFilters.createdAtStart
    ? DateTime.fromISO(lastQueryFilters.createdAtStart)
    : DateTime.local();
  const createdAtEnd = lastQueryFilters.createdAtEnd
    ? DateTime.fromISO(lastQueryFilters.createdAtEnd)
    : DateTime.local();

  const templateFilterOptions = useMemo(
    () =>
      templates.map((template) => ({
        label: nameDisplay(template),
        value: template.id,
      })),
    [templates],
  );

  const templateFilterLabelTextMapper = useLabelTextMapper(templateFilterOptions, 'templates', {
    emptyIsAll: true,
  });

  const {
    [FilterDimensions.CountryCode]: countryCodesOptions,
    [FilterDimensions.DeviceGroup]: deviceGroupsOptions,
    [FilterDimensions.PlatformIntegration]: platformOptions,
  } = dynamicFilters;

  const sortedCountryCodeOptions = useMemo(
    () => countryCodesOptions.sort((a, b) => (a.value > b.value ? 1 : -1)),
    [countryCodesOptions],
  );

  const tagFilterOptions = useMemo(
    () =>
      inquiryTags.map((tag) => ({
        label: tag.attributes.name,
        value: tag.id,
      })),
    [inquiryTags],
  );

  const tagFilterLabelTextMapper = useLabelTextMapper(tagFilterOptions, 'tags', {
    emptyIsAll: true,
  });

  const selectedTags = useMemo(
    () => findSelectedTags(lastQueryFilters, inquiryTags),
    [lastQueryFilters, inquiryTags],
  );

  const sortedDeviceGroupsOptions = useMemo(
    () => deviceGroupsOptions.sort((a, b) => (a.value > b.value ? 1 : -1)),
    [deviceGroupsOptions],
  );

  const sortedPlatformOptions = useMemo(
    () => platformOptions.sort((a, b) => (a.value > b.value ? 1 : -1)),
    [platformOptions],
  );

  const codeFilterLabelTextMapper = useLabelTextMapper(countryCodesOptions, 'countries', {
    emptyIsAll: true,
  });

  const platformFilterLabelTextMapper = useLabelTextMapper(platformOptions, 'integrations', {
    emptyIsAll: true,
  });

  const deviceFilterLabelTextMapper = useLabelTextMapper(deviceGroupsOptions, 'groups', {
    emptyIsAll: true,
  });

  return (
    <ModalContainer size='md'>
      <ModalHeader onClose={isPending ? undefined : cancelAction}>Export Data</ModalHeader>

      <ModalBody>
        <StyledDivider />
        <Spacer height={2} />
        <Paragraph>
          This will create a detailed CSV for individual Inquiries whose aggregate metrics are
          displayed on the Inquiry Analytics Page.{' '}
          <a role='link' href={HC_ARTICLE} target='_blank' rel='noreferrer'>
            Learn more.
          </a>
        </Paragraph>
        <Spacer height={2} />
        <FormControlLabel>
          Dates ({DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})
        </FormControlLabel>
        <DateRangeInputs>
          <DateField
            inputPosition='start'
            focused={false}
            readOnly={true}
            disabled={true}
            value={new CalendarDate(createdAtStart.year, createdAtStart.month, createdAtStart.day)}
          />
          <DateField
            inputPosition='end'
            focused={false}
            readOnly={true}
            disabled={true}
            value={new CalendarDate(createdAtEnd.year, createdAtEnd.month, createdAtEnd.day)}
          />
        </DateRangeInputs>
        {templates.length > 1 && (
          <>
            <Spacer height={2} />
            <FormControlLabel>Inquiry templates</FormControlLabel>
            <MultiSelect
              value={lastQueryFilters.templateOrInquiryTemplateIds}
              selectOptionValues={templateFilterOptions}
              labelTextMapper={templateFilterLabelTextMapper}
              onChange={() => undefined}
              disabled={true}
              size='md'
            />
          </>
        )}
        <Spacer height={2} />
        <FormControlLabel>Country codes</FormControlLabel>
        <MultiSelect
          value={lastQueryFilters.countryCodes}
          selectOptionValues={sortedCountryCodeOptions}
          labelTextMapper={codeFilterLabelTextMapper}
          onChange={() => undefined}
          disabled={true}
          size='md'
          searchEnabled
        />
        <Spacer height={2} />
        <FormControlLabel>Platform integrations</FormControlLabel>
        <MultiSelect
          value={lastQueryFilters.platforms}
          selectOptionValues={sortedPlatformOptions}
          labelTextMapper={platformFilterLabelTextMapper}
          onChange={() => undefined}
          disabled={true}
          size='md'
          searchEnabled
        />
        <Spacer height={2} />
        <FormControlLabel>Device groups</FormControlLabel>
        <MultiSelect
          value={lastQueryFilters.deviceGroups}
          selectOptionValues={sortedDeviceGroupsOptions}
          labelTextMapper={deviceFilterLabelTextMapper}
          onChange={() => undefined}
          disabled={true}
          size='md'
          searchEnabled
        />
        {tagFilterOptions.length > 0 && (
          <>
            <Spacer height={2} />
            <FormControlLabel>Tags</FormControlLabel>
            <MultiSelect
              value={selectedTags.map((tag) => tag.id)}
              selectOptionValues={tagFilterOptions}
              labelTextMapper={tagFilterLabelTextMapper}
              onChange={() => undefined}
              disabled={true}
              size='md'
              searchEnabled
            />
          </>
        )}
        <Spacer height={2} />
        <ToggleSlider
          disabled={true}
          labelOff='Group By Accounts'
          labelOn='Group By Accounts'
          onChange={() => undefined}
          value={lastQueryFilters.groupBy === MetricsGroupBy.Accounts}
        />
        {warningModal != null && (
          <>
            <Spacer height={2} />
            <Banner variant='warning' style={{ padding: Spacing[4] }}>
              <WarningIcon>
                <CircleInfoIcon />
              </WarningIcon>
              {warningModal}
            </Banner>
          </>
        )}
        <Spacer height={2} />
        <StyledDivider />
        <Spacer height={2} />
        <FormControlLabel>File Type</FormControlLabel>
        <Select
          id='inquiry-analytics__exporter-file-type-select'
          value={exporterFileType}
          onValueChange={(fileType) => {
            setExporterFileType(fileType as ExporterFileType);
          }}
        >
          {Object.values(ExporterFileType).map((fileType) => (
            <Option key={`inquiry-analytics__export__${fileType}`} value={fileType}>
              {fileType}
            </Option>
          ))}
        </Select>
        <Spacer height={4} />
        <ExportButtonGroup equal>
          <Button
            autoFocus={true}
            id='inquiry-confirmation-modal__cancel'
            variant='basic'
            onClick={cancelAction}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            id='inquiry-confirmation-modal__confirm'
            variant={confirmButtonType}
            onClick={handleConfirm}
            isPending={isPending}
            disabled={isPending}
          >
            Export
          </Button>
        </ExportButtonGroup>
      </ModalBody>
    </ModalContainer>
  );
};

export default InquiryAnalyticsExportModal;
